import React from "react";
import type { PlanCardCarouselProps } from "dds/organisms/plan-card/plan-card-carousel";
import { ZipCodeLazy } from "../v3-zip-code/zip-code-lazy.client";
import { toRenderablePlanCard } from "./renderable-plan-card";
import { anyCardReprices, shouldShowZipCode, type ZipDependentCard } from "../v3-zip-code/show-zip-code";
import { APP_BASE_PATH } from "src/utils/app-base-path";
import { splitZipCopy, type ZipCopyFields } from "../v3-zip-code/zip-copy";

/**
 * A card as the canvas receives it: the organism's props, plus the fields that belong
 * to THIS blok rather than to `PlanCard`. `price` and `package_id` are author-facing and
 * must reach the CMS team, but neither is an organism prop — the filler splits one into
 * `price_dollars` / `price_cents`, the adapter reads the other into `dp_all_in` — so
 * declaring them on `PlanCardProps` would publish two fields the design system neither
 * accepts nor renders. Both are documented on the carousel's `cards` ArgType instead,
 * which is what the CMS team reads.
 *
 * They ARE still spread through to the organism, unread: `PlanCard` has no rest spread,
 * so an undeclared key reaches no DOM node and renders nothing. Only `dp_all_in` is
 * stripped, because it is page data rather than authored content. Neither takes a
 * suffix: `price` sits with its rendered siblings, `package_id` is a feed key.
 */
type CarouselCard = ZipDependentCard & {
  legal_content?: unknown;
  /** The whole price as one authored string, e.g. `$64.99/mo.*`, split server-side. */
  price?: unknown;
  /** The feed's package id, e.g. `ATT_TV_CHOICE`. Drives the re-pricing recipe. */
  package_id?: unknown;
};

export type PlanSlotProps =
  Omit<PlanCardCarouselProps, "cards"> & ZipCopyFields & {
    cards?: CarouselCard[];
    /** `true` / `"true"` forces the ZIP on. Anything else — `false`, an untouched
     *  switch, an absent field — defers to `anyCardReprices`. The switch cannot take the
     *  control away; `show-zip-code.ts` carries why. */
    show_zip_code?: boolean | string;
    /** WAT-12293 renamed this to `show_zip_code`. DECLARED ONLY SO IT CAN BE DROPPED —
     *  it is not read, because `CLAUDE.md` wants no back-compat shim that nobody asked
     *  for, and because honouring it correctly is subtler than it looks: `?? ` falls
     *  through on `undefined` alone, so once `show_zip_code` is in the schema and
     *  publishing `false`, a `??` chain would ignore the old key exactly when it
     *  mattered. The key is not on `master`; a test or staging blok carrying it must be
     *  re-authored under the new name. */
    is_search_zip_code?: boolean | string;
  };

/**
 * The Signature plan-card carousel's slot props (WAT-11988 AC8). `SLOT_FILLERS` runs
 * this before the canvas renders the organism, for two things CMS JSON cannot express:
 * `zip_code` takes a NODE, and the cards' all-in prices have to re-price in the browser
 * when the visitor's ZIP changes.
 *
 * ⚠ THE ORGANISM IS A TYPE-ONLY IMPORT, and must stay one — see the note in
 * `fill-grow-slots.tsx`. A value import here drags `PlanCardCarousel` into every v3
 * page's shared chunk.
 *
 * No adapter runs on this path. The blok is authored in the organism's own prop names
 * and `injectDynamicValues` painted every price and legal string at cache-populate
 * time; the one thing added upstream is `dp_all_in`, the re-pricing recipe
 * `dynamic-page-components-adapter` builds from the package id.
 *
 * The slot is GATED BY DEFAULT, on the rule all three carousels now share: a carousel
 * whose prices cannot move should not offer a ZIP. An authored `show_zip_code`
 * overrides that gate ONE WAY ONLY — on. An untouched Storyblok switch publishes `false`, so a `false` that
 * hid the control would take it off every already-published page the day the field is
 * added to the blok. `autoOpenOnFirstVisit` is off and the local-channels route is
 * prefixed for the reasons in `fill-grow-slots.tsx`.
 *
 * ⚠ BOTH SWITCH KEYS ARE DESTRUCTURED OFF: they are blok-only fields the organism
 * never declared, and spreading either through would publish an undeclared prop.
 */
export function fillPlanSlots({
  zip_code: _authoredZipCode,
  show_zip_code: showZipCode,
  is_search_zip_code: _legacyShowZipCode,
  ...props
}: PlanSlotProps): PlanCardCarouselProps {
  const { copy, rest } = splitZipCopy(props);
  const cards = Array.isArray(rest.cards) ? rest.cards : [];
  const showZip = shouldShowZipCode(showZipCode, anyCardReprices(cards));

  /* `dp_all_in` is page data, not an organism prop, so `toRenderablePlanCard` strips it.
     That helper also carries the `?? {}` null-hole guard, which must agree with
     `anyCardReprices` above — it tolerates a hole in `cards`, and a destructure
     that did not would throw on the server and blank the page. */
  const planCards = cards.map(toRenderablePlanCard);

  return {
    ...rest,
    cards: planCards,
    zip_code: showZip ?
      <ZipCodeLazy {...copy} autoOpenOnFirstVisit={false} localChannelsBasePath={APP_BASE_PATH} /> :
      undefined
  } as PlanCardCarouselProps;
}

export default fillPlanSlots;
